import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { getApiBaseUrlWithFallback, getDomainBranding, shouldUseDefaultBranding } from '../config/environment'; // Import environment config
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Alert,
  Chip,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Gavel as RulesIcon,
  Speed as SpeedIcon,
  HowToReg as RegisterIcon,
  Help as HelpIcon,
  Email as EmailIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
// Legacy data link removed - now handled during Add Skater flow
import registrationService from '../services/registrationService';
import { API_BASE_URL } from '../config/environment';
import { isSeasonRegistrationOpen } from '../utils/seasonRegistration';

export default function Home() {
  const navigate = useNavigate();
  const { user, getIdToken } = useFirebaseAuth();
  const [upcomingCompetitions, setUpcomingCompetitions] = useState([]);
  const [competitionsLoading, setCompetitionsLoading] = useState(true);
  const [skaters, setSkaters] = useState([]);
  const [skatersLoading, setSkatersLoading] = useState(false);
  const [skatersError, setSkatersError] = useState(null);
  const [selectedSkater, setSelectedSkater] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreError, setScoreError] = useState(null);
  const [currentSeasonYear, setCurrentSeasonYear] = useState(null);
  const [currentSeasonName, setCurrentSeasonName] = useState('');
  const [currentSeasonId, setCurrentSeasonId] = useState('');
  const [currentSeasonCompetitionNames, setCurrentSeasonCompetitionNames] = useState([]);
  const [currentSeasonCompetitions, setCurrentSeasonCompetitions] = useState([]);
  const [brandingTitle, setBrandingTitle] = useState('');
  const [brandingDescription, setBrandingDescription] = useState('');
  const [brandingLogoUrl, setBrandingLogoUrl] = useState('');
  const [helpTitle, setHelpTitle] = useState('');
  const [helpContent, setHelpContent] = useState('');
  const [helpEmail, setHelpEmail] = useState('');
  const [seasonRegistrationOpen, setSeasonRegistrationOpen] = useState(false);
  const [seasonEntryFee, setSeasonEntryFee] = useState(null);
  const [seasonFeeDescription, setSeasonFeeDescription] = useState('');
  const [seasonRegistrations, setSeasonRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(true);
  const [qualifiersEnabled, setQualifiersEnabled] = useState(false);

  // Helper to parse dates without timezone conversion issues
  // Dates are stored as UTC midnight, so we need to extract date parts and create local date
  const parseDate = (dateObj) => {
    if (!dateObj) return null;
    if (dateObj._seconds !== undefined) {
      // Firestore timestamp - convert to UTC date parts then create local date
      const utcDate = new Date(dateObj._seconds * 1000);
      return new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
    }
    if (dateObj instanceof Date) {
      return dateObj;
    }
    if (typeof dateObj === 'string') {
      // Parse ISO date strings (YYYY-MM-DD) without timezone shift
      const isoMatch = dateObj.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, year, month, day] = isoMatch;
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    }
    return new Date(dateObj);
  };
  
  useEffect(() => {
    const fetchUpcomingCompetitions = async () => {
      try {
        // Get the appropriate API base URL with fallback
        const baseUrl = await getApiBaseUrlWithFallback();
        const response = await fetch(`${baseUrl}/competitions`);
        if (response.ok) {
          const competitions = await response.json();
          console.log('All competitions:', competitions);
          
          const now = new Date();
          console.log('Current date:', now);
          
          const upcoming = competitions
            .filter(comp => {
              // Parse dates using helper to avoid timezone issues
              const startDate = parseDate(comp.startDate || comp.date);
              if (!startDate) {
                console.log('No valid date for competition:', comp);
                return false;
              }
              
              // Get end date if available
              const endDate = parseDate(comp.endDate) || startDate;
              
              console.log(`Competition ${comp.name}: ${startDate} to ${endDate}`);
              
              // Include competitions that are current (ongoing) or upcoming
              return endDate >= now;
            })
            .sort((a, b) => {
              const dateA = parseDate(a.startDate || a.date);
              const dateB = parseDate(b.startDate || b.date);
              return dateA - dateB;
            })
            .slice(0, 3);
          
          console.log('Filtered upcoming competitions:', upcoming);
          setUpcomingCompetitions(upcoming);
        }
      } catch (error) {
        console.error('Error fetching competitions:', error);
      } finally {
        setCompetitionsLoading(false);
      }
    };

    fetchUpcomingCompetitions();
  }, []);

  const calculateTotalScore = useCallback((data, isCompEligible) => {
    const sortedScores = (data.competitions || [])
      .filter((comp) => (isCompEligible ? isCompEligible(comp) : true))
      .map((comp) => comp.points || 0)
      .sort((a, b) => b - a);
    return sortedScores.slice(0, 3).reduce((sum, score) => sum + score, 0);
  }, []);

  const getQualificationStatus = useCallback((data, isCompEligible) => {
    if (!qualifiersEnabled) {
      return null;
    }

    const counting = (data.competitions || []).filter((comp) =>
      isCompEligible ? isCompEligible(comp) : true
    );
    const totalScore = calculateTotalScore(data, isCompEligible);
    const competitionCount = counting.length;
    if (competitionCount < 3) {
      return {
        status: 'incomplete',
        message: `Needs ${3 - competitionCount} more competition${3 - competitionCount === 1 ? '' : 's'}`,
        color: 'warning.main',
        bgColor: 'warning.50'
      };
    }

    if (totalScore >= 15) {
      return {
        status: 'likely',
        message: 'Likely to qualify for championship',
        color: 'success.main',
        bgColor: 'success.50'
      };
    }
    if (totalScore >= 10) {
      return {
        status: 'borderline',
        message: 'Borderline qualification status',
        color: 'info.main',
        bgColor: 'info.50'
      };
    }
    return {
      status: 'unlikely',
      message: 'Unlikely to qualify with current scores',
      color: 'warning.main',
      bgColor: 'warning.50'
    };
  }, [calculateTotalScore, qualifiersEnabled]);

  const loadSkaters = useCallback(async () => {
    if (!user) return;
    try {
      setSkatersLoading(true);
      setSkatersError(null);
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      const skatersData = await registrationService.getSkaters(token);
      setSkaters(skatersData);
    } catch (error) {
      console.error('Error loading skaters:', error);
      setSkatersError('Failed to load skaters.');
    } finally {
      setSkatersLoading(false);
    }
  }, [user, getIdToken]);

  useEffect(() => {
    loadSkaters();
  }, [loadSkaters]);

  // Load season registrations to show registration status
  useEffect(() => {
    const loadRegistrations = async () => {
      if (!user || !currentSeasonId || skaters.length === 0) {
        setRegistrationsLoading(false);
        return;
      }
      setRegistrationsLoading(true);
      try {
        const token = await getIdToken(true);
        if (!token) {
          setRegistrationsLoading(false);
          return;
        }
        const skaterIds = skaters.map((s) => s.id).filter(Boolean);
        const registrations = await registrationService.getSeasonRegistrations(currentSeasonId, token, skaterIds);
        setSeasonRegistrations(registrations);
      } catch (error) {
        console.error('Error loading registrations:', error);
      } finally {
        setRegistrationsLoading(false);
      }
    };
    loadRegistrations();
  }, [user, currentSeasonId, skaters, getIdToken]);

  useEffect(() => {
    const loadCurrentSeasons = async () => {
      try {
        const baseUrl = await getApiBaseUrlWithFallback();
        const response = await fetch(`${baseUrl}/seasons?current=true`, {
          method: 'GET',
          mode: 'cors'
        });
        if (!response.ok) {
          return;
        }
        const seasons = await response.json();
        const currentSeasons = seasons.filter((season) => season.isCurrent);
        if (currentSeasons.length === 0) return;
        const season = currentSeasons[0];
        const seasonId = season.id || '';
        // Use component-level parseDate for timezone-safe parsing
        const startDate = parseDate(season.startDate);
        const endDate = parseDate(season.endDate);
        // Keep year list logic local if needed later, but only store a single year for filtering.
        const seasonYear =
          endDate?.getFullYear()
          || startDate?.getFullYear()
          || (() => {
            const match = (season.name || '').match(/(\d{4})\D+(\d{4})/);
            return match ? parseInt(match[2], 10) : null;
          })();
        setCurrentSeasonYear(seasonYear);
        setCurrentSeasonName(season.name || '');
        setCurrentSeasonId(seasonId);
        setSeasonRegistrationOpen(isSeasonRegistrationOpen(season));

        // Check domain-based branding first
        const domainBranding = getDomainBranding();
        
        if (shouldUseDefaultBranding()) {
          // Use default branding for main domain (interclubseries.com)
          setBrandingTitle(domainBranding.brandingTitle || 'Interclub Competition Manager');
          setBrandingDescription(domainBranding.brandingDescription || '');
          setBrandingLogoUrl(''); // Use default logo
          // Still load interclub settings for fees, help, etc. if available
          if (season.interclubId) {
            try {
              const interclubResponse = await fetch(`${baseUrl}/interclubs/${season.interclubId}`);
              if (interclubResponse.ok) {
                const interclub = await interclubResponse.json();
                setHelpTitle(interclub?.helpTitle || '');
                setHelpContent(interclub?.helpContent || '');
                setHelpEmail(interclub?.helpEmail || interclub?.contact?.email || '');
                const fee = season.seasonEntryFee ?? interclub?.seasonEntryFee ?? null;
                setSeasonEntryFee(fee);
                setSeasonFeeDescription(interclub?.seasonFeeDescription || '');
                setQualifiersEnabled(interclub?.enableQualifiers !== false);
              }
            } catch (err) {
              console.error('Error loading interclub settings:', err);
            }
          }
        } else if (season.interclubId) {
          // Load branding from interclub (for subdomains like pnic.interclubseries.com)
          try {
            const interclubResponse = await fetch(`${baseUrl}/interclubs/${season.interclubId}`);
            if (interclubResponse.ok) {
              const interclub = await interclubResponse.json();
              setBrandingTitle(interclub?.brandingTitle || '');
              setBrandingDescription(interclub?.brandingDescription || '');
              setBrandingLogoUrl(interclub?.logoUrl || '');
              setHelpTitle(interclub?.helpTitle || '');
              setHelpContent(interclub?.helpContent || '');
              setHelpEmail(interclub?.helpEmail || interclub?.contact?.email || '');
              // Get season entry fee (season-specific or interclub default)
              const fee = season.seasonEntryFee ?? interclub?.seasonEntryFee ?? null;
              setSeasonEntryFee(fee);
              setSeasonFeeDescription(interclub?.seasonFeeDescription || '');
              // Check if qualifiers are enabled for this interclub
              setQualifiersEnabled(interclub?.enableQualifiers !== false);
            }
          } catch (brandingError) {
            console.error('Error loading interclub branding:', brandingError);
          }
        }

        if (seasonId) {
          const competitionsResponse = await fetch(`${baseUrl}/seasons/${seasonId}/competitions`);
          if (competitionsResponse.ok) {
            const competitions = await competitionsResponse.json();
            const names = competitions.map((competition) => competition.name).filter(Boolean);
            setCurrentSeasonCompetitionNames(names);
            setCurrentSeasonCompetitions(competitions);
          }
        }
      } catch (error) {
        console.error('Error loading current seasons:', error);
      }
    };
    loadCurrentSeasons();
  }, []);

  const getSeasonLevels = useCallback((skater) => {
    if (!currentSeasonId) {
      return {
        startingLevel: skater.startingLevel,
        currentLevel: skater.currentLevel,
        levelChanged: false,
      };
    }
    const seasonLevels = skater.seasonLevels?.[currentSeasonId];
    return {
      startingLevel: seasonLevels?.startingLevel || skater.startingLevel,
      currentLevel: seasonLevels?.currentLevel || skater.currentLevel,
      levelChanged: seasonLevels?.levelChanged || false,
    };
  }, [currentSeasonId]);

  const handleViewResults = async (skater) => {
    if (!skater?.membershipId) {
      setScoreError('Missing USFS ID for this skater.');
      return;
    }
    setSelectedSkater(skater);
    setScoreError(null);
    setScoreData(null);
    setScoreLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/get-score/${skater.membershipId}`);
      if (!response.ok) {
        throw new Error('Unable to load results');
      }
      const data = await response.json();
      const normalizeName = (value) => (value || '').toLowerCase().replace(/^\d{4}\s+/, '').trim();
      const nameSet = new Set(currentSeasonCompetitionNames.map((name) => normalizeName(name)));
      const seasonCompetitionMap = new Map(
        currentSeasonCompetitions
          .filter((competition) => competition.name)
          .map((competition) => [normalizeName(competition.name), competition])
      );
      if (nameSet.size > 0) {
        data.competitions = data.competitions.filter((comp) => {
          const rawName = (comp.competitionName || '').trim();
          const normalized = normalizeName(rawName);
          if (!nameSet.has(normalized)) {
            return false;
          }
          const match = seasonCompetitionMap.get(normalized);
          if (!match) return false;
          const yearMatch = rawName.match(/^(\d{4})\s+/);
          if (yearMatch) {
            const compYear = parseInt(yearMatch[1], 10);
            const startValue = match.startDate || match.date;
            let seasonYear = null;
            if (startValue?._seconds) {
              seasonYear = new Date(startValue._seconds * 1000).getFullYear();
            } else if (startValue?.toDate) {
              seasonYear = startValue.toDate().getFullYear();
            } else if (startValue) {
              const parsed = new Date(startValue);
              seasonYear = Number.isNaN(parsed.getTime()) ? null : parsed.getFullYear();
            }
            if (seasonYear && compYear !== seasonYear) {
              return false;
            }
          }
          return true;
        });
      } else if (currentSeasonYear) {
        const yearStrings = [String(currentSeasonYear)];
        data.competitions = data.competitions.filter((comp) =>
          yearStrings.some((year) => (comp.competitionName || '').includes(year))
        );
      }
      setScoreData(data);
    } catch (error) {
      console.error('Error loading score data:', error);
      setScoreError('Failed to load competition results.');
    } finally {
      setScoreLoading(false);
    }
  };
  
  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: 'none',
      // Ensure content fills the full width
      px: 0,
      mx: 0,
      boxSizing: 'border-box'
    }}>
      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          mb: { xs: 3, sm: 4 },
          textAlign: 'center',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.04) 100%)',
        }}
      >
        {brandingLogoUrl ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              component="img"
              src={brandingLogoUrl}
              alt={`${brandingTitle || 'Interclub'} Logo`}
              onError={(event) => {
                if (event.currentTarget.src.includes('/logo192.png')) return;
                event.currentTarget.src = `${process.env.PUBLIC_URL}/logo192.png`;
              }}
              sx={{ height: 80, width: 80, borderRadius: 2, objectFit: 'cover' }}
            />
          </Box>
        ) : (
          <Box sx={{ 
            display: 'inline-flex', 
            p: 2, 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            mb: 2,
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
          }}>
            <TrophyIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        )}
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: 'text.primary' }}>
          {brandingTitle || 'Interclub Competition Manager'}
        </Typography>
        {currentSeasonName && (
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
            Season: {currentSeasonName}
          </Typography>
        )}
        <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 860, mx: 'auto' }}>
          {brandingDescription
            ? brandingDescription
            : `Welcome to ${brandingTitle || 'the Interclub Competition Manager'}! Discover upcoming competitions, check qualifiers, and track your progress.`}
        </Typography>
      </Paper>

      {/* Season Cost Section - only show if fee is configured */}
      {seasonEntryFee && (
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 4, 
            p: 4, 
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(180deg, rgba(17,153,142,0.04) 0%, rgba(56,239,125,0.08) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
            }}>
              <PaymentIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Season Entry Fee
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: 'success.main' }}>
                ${seasonEntryFee} per skater
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 800 }}>
            {seasonFeeDescription || 
              'The season entry fee registers your skater for the interclub championship series. Your competition results will count towards championship qualification. Note: Individual competition entry fees are paid separately at each event.'}
          </Typography>
          
          {seasonRegistrationOpen ? (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(user ? '/registration' : '/auth')}
              sx={{ 
                fontWeight: 700, 
                borderRadius: 999, 
                textTransform: 'none',
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                boxShadow: '0 4px 14px rgba(17, 153, 142, 0.4)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #0e8a7d 0%, #2ed96c 100%)',
                  boxShadow: '0 6px 20px rgba(17, 153, 142, 0.5)',
                }
              }}
            >
              {user ? 'Register Now' : 'Get Started'}
            </Button>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Season registration is closed until the next season is set up. You can still create an account and view past results.
              </Alert>
              {!user && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/auth')}
                  sx={{ fontWeight: 700, borderRadius: 999, textTransform: 'none', px: 4, py: 1.5 }}
                >
                  Create an account
                </Button>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Help Section - only show if help email is configured */}
      {helpEmail && (
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 4, 
            p: 4, 
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(180deg, rgba(25,118,210,0.04) 0%, rgba(25,118,210,0.08) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <HelpIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {helpTitle || 'Need Help?'}
            </Typography>
          </Box>
          
          {helpContent && (
            <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 800 }}>
              {helpContent}
            </Typography>
          )}
          
          {!helpContent && (
            <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 800 }}>
              Have questions about registration, competitions, or need assistance? 
              We're here to help! Contact our support team and we'll get back to you as soon as possible.
            </Typography>
          )}
          
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<EmailIcon />}
            href={`mailto:${helpEmail}`}
            sx={{ 
              fontWeight: 700, 
              borderRadius: 999, 
              textTransform: 'none',
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
              '&:hover': { 
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
              }
            }}
          >
            Contact Us
          </Button>
        </Paper>
      )}

      {/* Skaters */}
      {user && (
        <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, mb: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
            }}>
              <PersonIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Skaters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Quick access to results and season progress
              </Typography>
            </Box>
          </Box>
          {skatersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : skatersError ? (
            <Alert severity="error">{skatersError}</Alert>
          ) : skaters.length === 0 ? (
            <Typography variant="body1">No skaters found yet.</Typography>
          ) : (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {skaters.map((skater) => {
                const isRegistered = seasonRegistrations.some((r) => r.skaterId === skater.id);
                const isPaused = currentSeasonId && (skater.pausedSeasons || []).includes(currentSeasonId);
                const getStatusChip = () => {
                  if (isPaused) return { label: 'Paused', color: 'default' };
                  if (isRegistered) return { label: 'Registered', color: 'success' };
                  return { label: 'Not Registered', color: 'warning' };
                };
                const statusChip = getStatusChip();
                return (
                  <Grid item xs={12} md={6} key={skater.id}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, ...(isPaused && { opacity: 0.7 }) }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {skater.firstName} {skater.lastName}
                        </Typography>
                        {currentSeasonId && (
                          registrationsLoading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Chip
                              label={statusChip.label}
                              color={statusChip.color}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        USFS ID: {skater.membershipId || 'Not provided'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Club: {skater.club || 'Not specified'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {(() => {
                          const seasonLevels = getSeasonLevels(skater);
                          if (
                            seasonLevels.levelChanged
                            && seasonLevels.startingLevel
                            && seasonLevels.currentLevel
                            && seasonLevels.startingLevel !== seasonLevels.currentLevel
                          ) {
                            return `Level: ${seasonLevels.startingLevel} → ${seasonLevels.currentLevel}`;
                          }
                          return `Level: ${seasonLevels.currentLevel || seasonLevels.startingLevel || 'Not specified'}`;
                        })()}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                          onClick={() => handleViewResults(skater)}
                          disabled={!skater.membershipId}
                        >
                          View Results
                        </Button>
                        {!registrationsLoading && !isRegistered && !isPaused && currentSeasonId && seasonRegistrationOpen && (
                          <Button
                            variant="contained"
                            size="small"
                            color="warning"
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                            onClick={() => navigate('/registration')}
                          >
                            Register Now
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {scoreLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {scoreError && <Alert severity="error">{scoreError}</Alert>}
          {scoreData && (() => {
            // Get registration info for this skater
            const registration = seasonRegistrations.find((r) => r.skaterId === selectedSkater?.id);
            const registrationDate = registration?.paidAt?._seconds
              ? new Date(registration.paidAt._seconds * 1000)
              : registration?.paidAt?.toDate?.()
              || (registration?.paidAt ? new Date(registration.paidAt) : null);
            
            // Check if any competitions happened before registration
            const getCompetitionEligibility = (compName) => {
              if (!registrationDate) return { eligible: true, reason: null };
              const competition = currentSeasonCompetitions.find(
                (c) => c.name === compName || c.shortName === compName
              );
              if (!competition) return { eligible: true, reason: null };
              
              const compEndDate = parseDate(competition.endDate || competition.date);
              
              if (compEndDate && registrationDate > compEndDate) {
                return {
                  eligible: false,
                  reason: 'Registered after competition ended',
                };
              }
              return { eligible: true, reason: null };
            };
            
            const hasIneligibleCompetitions = scoreData.competitions?.some(
              (comp) => !getCompetitionEligibility(comp.competitionName).eligible
            );

            return (
            <Box mt={2} p={2} border={1} borderColor="grey.300" borderRadius={2}>
              <Typography variant="h6" fontWeight="bold">
                {selectedSkater?.firstName} {selectedSkater?.lastName}
              </Typography>
              {currentSeasonName && (
                <Typography variant="subtitle2" color="text.secondary">
                  {currentSeasonName} Results
                </Typography>
              )}
              
              {/* Late Registration Warning */}
              {registrationDate && hasIneligibleCompetitions && (
                <Alert severity="warning" sx={{ my: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Late Registration Notice
                  </Typography>
                  <Typography variant="body2">
                    Some competitions occurred before registration and do not count toward season scoring.
                  </Typography>
                </Alert>
              )}
              
              <Typography variant="subtitle1">
                <strong>Club:</strong> {scoreData.skatingClub}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Starting Level:</strong>{' '}
                {(() => {
                  const seasonLevels = getSeasonLevels(selectedSkater || {});
                  // If skater is USFS verified, always use Firestore data
                  const firstResultLevel = (scoreData.competitions || []).find((c) => c.level)?.level;
                  if (firstResultLevel) return firstResultLevel;
                  if (selectedSkater?.usfsVerified) {
                    return seasonLevels.startingLevel || selectedSkater.startingLevel || 'Not Set';
                  }
                  return seasonLevels.startingLevel || scoreData.startingCompetitionLevel || 'Not Set';
                })()}
              </Typography>
              <Typography variant="subtitle1">
                <strong>Last Competition Level:</strong>{' '}
                {(() => {
                  const seasonLevels = getSeasonLevels(selectedSkater || {});
                  // If skater is USFS verified, always use Firestore data
                  const resultLevels = (scoreData.competitions || []).filter((c) => c.level);
                  const lastResultLevel = resultLevels[resultLevels.length - 1]?.level;
                  if (lastResultLevel) return lastResultLevel;
                  if (selectedSkater?.usfsVerified) {
                    return seasonLevels.currentLevel || selectedSkater.currentLevel || 'Not Set';
                  }
                  return seasonLevels.currentLevel || scoreData.competitionLevel || 'Not Set';
                })()}
              </Typography>
              <Typography variant="h6" fontWeight="bold" mt={2}>
                Competition Points:
              </Typography>
              {scoreData.competitions.length === 0 ? (
                <ul>
                  <li>
                    <strong>No Competitions Recorded</strong>
                  </li>
                </ul>
              ) : (
                <Box component="ul" sx={{ textAlign: 'left', pl: '20px', m: 0 }}>
                  {scoreData.competitions.map((comp, index) => {
                    const compEligibility = getCompetitionEligibility(comp.competitionName);
                    const isEligible = comp.isEligible !== undefined ? comp.isEligible : compEligibility.eligible;
                    const eligibilityReason = !isEligible
                      ? (comp.eligibility && comp.eligibility !== 'Eligible'
                        ? comp.eligibility
                        : compEligibility.reason || '')
                      : '';
                    return (
                      <Box 
                        component="li" 
                        key={index} 
                        sx={{ 
                          mb: 0.5, 
                          opacity: isEligible ? 1 : 0.6,
                          color: isEligible ? 'inherit' : 'text.secondary'
                        }}
                      >
                        <strong>{comp.competitionName}:</strong> {comp.points} points
                        {!isEligible && (
                          <Chip
                            label={eligibilityReason || 'Ineligible'}
                            size="small"
                            color="default"
                            sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
              <Typography variant="h6" fontWeight="bold" mt={2}>
                Total Score:{' '}
                {calculateTotalScore(scoreData, (comp) => {
                  if (comp.isEligible !== undefined) return comp.isEligible;
                  return getCompetitionEligibility(comp.competitionName).eligible;
                })}
              </Typography>
              <Typography variant="subtitle2" mt={1} sx={{ mb: 2 }}>
                (Top 3 scores are used for qualification.)
              </Typography>
              {qualifiersEnabled && (() => {
                const qualStatus = getQualificationStatus(scoreData, (comp) => {
                  if (comp.isEligible !== undefined) return comp.isEligible;
                  return getCompetitionEligibility(comp.competitionName).eligible;
                });
                if (!qualStatus) return null;
                return (
                  <>
                    <Typography variant="h6" fontWeight="bold" mt={3}>
                      🏆 Championship Qualification Analysis:
                    </Typography>
                    <Box sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: qualStatus.bgColor,
                      border: 1,
                      borderColor: qualStatus.color
                    }}>
                      <Typography variant="body1" sx={{ color: qualStatus.color, fontWeight: 'bold' }}>
                        {qualStatus.status === 'likely' && '✅ Likely to Qualify'}
                        {qualStatus.status === 'borderline' && '⚠️ Borderline Status'}
                        {qualStatus.status === 'unlikely' && '❌ Unlikely to Qualify'}
                        {qualStatus.status === 'incomplete' && '⏳ Requirements Incomplete'}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <strong>Status:</strong> {qualStatus.message}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
            </Box>
            );
          })()}
        </Paper>
      )}
       
       {/* Quick Actions */}
       <Box sx={{ 
         display: 'flex', 
         flexDirection: { xs: 'column', md: 'row' },
         gap: 3,
         mb: 4,
         width: '100%'
       }}>
         {qualifiersEnabled && (
           <Box sx={{ 
             flex: { xs: 'none', md: 1 },
             width: '100%'
           }}>
             <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                 <Box sx={{ 
                   p: 1.5, 
                   borderRadius: 2, 
                   background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                   display: 'flex',
                   boxShadow: '0 4px 12px rgba(255, 193, 7, 0.4)'
                 }}>
                   <TrophyIcon sx={{ color: 'white', fontSize: 28 }} />
                 </Box>
                 <Typography variant="h5" sx={{ fontWeight: 700 }}>
                   Current Qualifiers
                 </Typography>
               </Box>
               <Typography variant="body1" color="text.secondary" paragraph>
                 View current qualifying standings and level advancement for all skaters.
               </Typography>
               <Button
                 variant="contained"
                 color="primary"
                 fullWidth
                 onClick={() => window.location.href = '/qualifiers'}
                 sx={{ fontWeight: 700, borderRadius: 999, textTransform: 'none' }}
               >
                 View Qualifiers
               </Button>
             </Paper>
           </Box>
         )}
       </Box>
       
       {/* White space below Quick Actions */}
       <Box sx={{ mb: 0.625 }} />
       
       {/* Current & Upcoming Competitions */}
      <Paper elevation={0} sx={{ p: { xs: 3, sm: 4 }, mb: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            display: 'flex',
            boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)'
          }}>
            <CalendarIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Current & Upcoming Competitions
          </Typography>
        </Box>
         
         {competitionsLoading ? (
           <Typography variant="body1" sx={{ textAlign: 'center' }}>
             Loading competitions...
           </Typography>
         ) : upcomingCompetitions.length > 0 ? (
           <Box sx={{ mb: 3 }}>
             {upcomingCompetitions.map((competition, index) => {
               // Determine if competition is current (ongoing) or upcoming
               const startDate = parseDate(competition.startDate || competition.date);
               const endDate = parseDate(competition.endDate) || startDate;
               
               const now = new Date();
               const isCurrent = startDate <= now && endDate >= now;
               const isUpcoming = startDate > now;
               
              const hasLinks = Boolean(
                competition.liveScoreLink || competition.registrationLink || competition.clubWebsiteLink
              );

              return (
                <Paper
                  key={competition.id || index}
                  elevation={0}
                  sx={{ 
                    p: 3,
                    mb: 2, 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 4,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                      borderColor: 'primary.light'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                    {/* Status indicator */}
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: isCurrent 
                        ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                        : isUpcoming 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: isCurrent 
                        ? '0 4px 12px rgba(17, 153, 142, 0.3)'
                        : '0 4px 12px rgba(102, 126, 234, 0.3)',
                      minWidth: 48,
                      minHeight: 48
                    }}>
                      <CalendarIcon sx={{ color: 'white', fontSize: 24 }} />
                    </Box>
                    
                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {competition.name}
                        </Typography>
                        <Chip
                          label={isCurrent ? 'Happening Now' : isUpcoming ? 'Coming Soon' : 'Scheduled'}
                          size="small"
                          sx={{ 
                            fontWeight: 600,
                            bgcolor: isCurrent ? 'success.50' : isUpcoming ? 'primary.50' : 'grey.100',
                            color: isCurrent ? 'success.dark' : isUpcoming ? 'primary.dark' : 'grey.700',
                            border: '1px solid',
                            borderColor: isCurrent ? 'success.200' : isUpcoming ? 'primary.200' : 'grey.300'
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                            {endDate && endDate.getTime() !== startDate.getTime() && 
                              ` - ${endDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`
                            }
                          </Typography>
                        </Box>
                        {competition.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SpeedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {competition.location}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      
                      {/* Event Links - inline buttons */}
                      {hasLinks && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {competition.liveScoreLink && (
                            <Button
                              variant="outlined"
                              size="small"
                              href={competition.liveScoreLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="success"
                              sx={{ 
                                textTransform: 'none', 
                                fontWeight: 600, 
                                borderRadius: 2,
                                px: 2,
                                borderWidth: 1.5,
                                '&:hover': {
                                  borderWidth: 1.5,
                                  bgcolor: 'success.50',
                                }
                              }}
                            >
                              Live Scores
                            </Button>
                          )}
                          {competition.registrationLink && (
                            <Button
                              variant="contained"
                              size="small"
                              href={competition.registrationLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                              sx={{ 
                                textTransform: 'none', 
                                fontWeight: 600, 
                                borderRadius: 2,
                                px: 2,
                              }}
                            >
                              Register
                            </Button>
                          )}
                          {competition.clubWebsiteLink && (
                            <Button
                              variant="text"
                              size="small"
                              href={competition.clubWebsiteLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ 
                                textTransform: 'none', 
                                fontWeight: 600, 
                                borderRadius: 2,
                                px: 2,
                                color: 'text.secondary',
                                '&:hover': {
                                  bgcolor: 'grey.100',
                                }
                              }}
                            >
                              Club Website
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
               );
             })}
           </Box>
         ) : (
           <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
             No current or upcoming competitions scheduled at this time.
           </Typography>
         )}
         
         <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => window.location.href = '/competitions'}
            sx={{ fontWeight: 700, borderRadius: 999, textTransform: 'none', px: 4 }}
          >
             VIEW ALL COMPETITIONS
           </Button>
         </Box>
       </Paper>
       
       {/* Qualifying Competitions - Only show if qualifier link is enabled */}
      {qualifiersEnabled && (
        <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
            }}>
              <TrophyIcon sx={{ color: 'white', fontSize: 28 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Qualifying Competitions
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            mt: 2,
            width: '100%'
          }}>
            {/* Competition 1 */}
            <Box sx={{ 
              flex: { xs: 'none', md: 1 },
              width: '100%'
            }}>
              <Box sx={{ p: 3, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Skate by the Lake
                </Typography>
                <Typography variant="body1" paragraph>
                  Hosted by Lake Washington Figure Skating Club
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Date:</strong> December 14-15, 2024
                </Typography>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => navigate('/qualifiers')}
                >
                  View Qualifiers
                </Button>
              </Box>
            </Box>

            {/* Competition 2 */}
            <Box sx={{ 
              flex: { xs: 'none', md: 1 },
              width: '100%'
            }}>
              <Box sx={{ p: 3, border: '2px solid', borderColor: 'secondary.main', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Apple Ice Classic
                </Typography>
                <Typography variant="body1" paragraph>
                  Hosted by Apple Valley Figure Skating Club
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  <strong>Date:</strong> January 18-19, 2025
                </Typography>
                <Button 
                  variant="outlined" 
                  color="secondary"
                  onClick={() => navigate('/qualifiers')}
                >
                  View Qualifiers
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Rules and Levels */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        gap: 3,
        mt: 2,
        width: '100%'
      }}>
        <Box sx={{ 
          flex: { xs: 'none', md: 1 },
          width: '100%'
        }}>
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                display: 'flex',
                boxShadow: '0 4px 12px rgba(250, 112, 154, 0.3)'
              }}>
                <RulesIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Competition Rules
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              Learn about the rules and regulations for interclub competitions
            </Typography>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                • Skaters must be current US Figure Skating members<br/>
                • Top 3 scores count toward qualification (one per host club)<br/>
                • Minimum 3 competitions required to qualify<br/>
                • Top 12 skaters per level qualify for championship
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => navigate('/rules')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
            >
              View Full Rules
            </Button>
          </Paper>
        </Box>
        <Box sx={{ 
          flex: { xs: 'none', md: 1 },
          width: '100%'
        }}>
          <Paper elevation={0} sx={{ p: 3, height: '100%', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                display: 'flex',
                boxShadow: '0 4px 12px rgba(245, 87, 108, 0.3)'
              }}>
                <SpeedIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Competition Levels
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" paragraph>
              Basic Skills Series competition levels
            </Typography>
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Youth:</strong> Snowplow Sam, Basic 1-6, Pre-Preliminary (Well Balanced)<br/>
                <strong>Adult:</strong> Adult 1-6
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => navigate('/rules')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
            >
              View Full Rules
            </Button>
          </Paper>
        </Box>
      </Box>

      {/* Get Started CTA - only show if not logged in or no skaters */}
      {(!user || skaters.length === 0) && (
        <Paper 
          elevation={0} 
          sx={{ 
            mt: 4, 
            p: 4, 
            textAlign: 'center', 
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ 
            display: 'inline-flex', 
            p: 2, 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            mb: 2,
            boxShadow: '0 8px 24px rgba(17, 153, 142, 0.3)'
          }}>
            <RegisterIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: 'text.primary' }}>
            Ready to Compete?
          </Typography>
          <Typography variant="body1" paragraph sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
            Join {currentSeasonName || brandingTitle || 'the Interclub Competition Manager'} and showcase your skating skills
          </Typography>
          {seasonEntryFee && (
            <Box sx={{ 
              display: 'inline-block', 
              bgcolor: 'success.50', 
              border: '1px solid',
              borderColor: 'success.main',
              borderRadius: 2, 
              px: 3, 
              py: 1.5, 
              mb: 3 
            }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark' }}>
                Season Entry Fee: ${seasonEntryFee} per skater
              </Typography>
            </Box>
          )}
          <Box>
            <Button 
              variant="contained" 
              size="large" 
              onClick={() => navigate('/auth')}
              sx={{ 
                fontSize: '1.1rem', 
                px: 4, 
                py: 1.5,
                fontWeight: 700,
                borderRadius: 999,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                }
              }}
            >
              Get Started
            </Button>
          </Box>
        </Paper>
      )}

      
      {/* White space below Get Started CTA */}
      <Box sx={{ mb: 2.5 }} />
    </Box>
  );
}